import React, { useState } from "react";
import "./ColorVariants.css";
import { toast } from "react-toastify";
import ColorPicker from "./ColorPicker";

// A single color variant is { name, hex, images } where images is a mix of
// File objects (newly picked, not uploaded yet) and plain URL strings
// (already-uploaded images, only present in edit mode).
export const emptyColor = () => ({ name: "", hex: "#7c3aed", images: [] });

const imgSrc = (img, backendUrl) => {
  if (typeof img === "string") return img.startsWith("http") ? img : `${backendUrl}${img}`;
  return URL.createObjectURL(img);
};

// Resolves a product image (File or URL string) to a Blob so it can be
// posted to the recolor endpoint regardless of where it came from.
const toBlob = async (img) => {
  if (typeof img !== "string") return img; // already a File/Blob
  const res = await fetch(img);
  return res.blob();
};

const NEW_COLOR = "__new__";

const ColorVariants = ({ colors, setColors, backendUrl, sourceImages = [] }) => {
  const [sourceIdx, setSourceIdx] = useState(0);
  // How the recolor tool tells "base fabric" apart from "printed pattern":
  // innerDeg = hue distance that's definitely base fabric (fully recolored).
  // outerDeg = hue distance that's definitely print (left untouched).
  // Between the two, it blends. Different fabrics need different values —
  // e.g. a print whose leaves sit close in hue to the base needs a smaller
  // outerDeg so those leaves don't get muddied by partial recoloring.
  const [innerDeg, setInnerDeg] = useState(22);
  const [outerDeg, setOuterDeg] = useState(55);

  const [pickerHex, setPickerHex] = useState("#7c3aed");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saveTarget, setSaveTarget] = useState(NEW_COLOR);
  const [newColorName, setNewColorName] = useState("");

  const addColor = () => setColors((p) => [...p, emptyColor()]);

  const removeColor = (i) => setColors((p) => p.filter((_, idx) => idx !== i));

  const updateColor = (i, field, value) =>
    setColors((p) => p.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  const addImages = (i, fileList) => {
    const files = Array.from(fileList);
    const current = colors[i].images;
    if (current.length + files.length > 4) {
      toast.error("Max 4 images per color!");
      return;
    }
    updateColor(i, "images", [...current, ...files]);
  };

  const removeImage = (i, imgIdx) =>
    updateColor(i, "images", colors[i].images.filter((_, idx) => idx !== imgIdx));

  const generatePreview = async () => {
    const source = sourceImages[sourceIdx];
    if (!source) {
      toast.error("Upload a product photo first, then generate a color from it");
      return;
    }
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const blob = await toBlob(source);
      const fd = new FormData();
      fd.append("source", blob, "source.jpg");
      fd.append("targetHex", pickerHex);
      fd.append("innerDeg", innerDeg);
      fd.append("outerDeg", outerDeg);
      const res = await fetch(`${backendUrl}/generatecolorvariant`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success) {
        setPreviewUrl(data.url);
      } else {
        toast.error(data.message || "Failed to generate preview");
      }
    } catch {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const saveGeneratedPreview = () => {
    if (!previewUrl) return;
    if (saveTarget === NEW_COLOR) {
      if (!newColorName.trim()) {
        toast.error("Give the new color a name first");
        return;
      }
      setColors((p) => [...p, { name: newColorName.trim(), hex: pickerHex, images: [previewUrl] }]);
      setNewColorName("");
    } else {
      const idx = Number(saveTarget);
      if (colors[idx].images.length >= 4) {
        toast.error("That color already has 4 photos — remove one first");
        return;
      }
      updateColor(idx, "images", [...colors[idx].images, previewUrl]);
      updateColor(idx, "hex", pickerHex);
    }
    toast.success("Saved as a swatch photo — still not published until you save the product");
    setPreviewUrl(null);
  };

  return (
    <div className="ap-card">
      <div className="ap-card-hd"><span>🎨</span> Color Variants</div>
      <p className="ap-img-hint">
        Add a swatch for every color this design comes in, each with its own photos.
        Customers pick a color on the product page and the photos switch to match.
        Leave this empty if the product doesn't come in multiple colors.
      </p>

      {sourceImages.length > 0 && (
        <div className="cv-generator">
          <span className="cv-generator-label">✨ Generate a color from a photo</span>
          <p className="cv-generator-hint">
            Recolors only the base fabric color and leaves any printed pattern alone. Preview it
            against the real photo before saving it as a swatch — it's a draft, not a substitute
            for a real photo when you have one.
          </p>

          <div className="cv-generator-row">
            <span>Source photo:</span>
            <select
              className="ap-select cv-source-select"
              value={sourceIdx}
              onChange={(e) => setSourceIdx(Number(e.target.value))}
            >
              {sourceImages.map((_, idx) => (
                <option key={idx} value={idx}>Photo {idx + 1}</option>
              ))}
            </select>
            <img
              className="cv-source-preview"
              src={imgSrc(sourceImages[sourceIdx], backendUrl)}
              alt={`Source photo ${sourceIdx + 1}`}
            />
          </div>

          <div className="cv-picker-row">
            <ColorPicker hex={pickerHex} onChange={setPickerHex} />

            <div className="cv-preview-pane">
              {previewLoading ? (
                <div className="cv-preview-placeholder">Rendering…</div>
              ) : previewUrl ? (
                <img className="cv-preview-img" src={previewUrl} alt="Recolor preview" />
              ) : (
                <div className="cv-preview-placeholder">Pick a color, then Preview</div>
              )}
              <button
                type="button"
                className="cv-add-color cv-preview-btn"
                onClick={generatePreview}
                disabled={previewLoading}
              >
                {previewLoading ? "Rendering…" : "👁 Preview"}
              </button>
            </div>
          </div>

          <div className="cv-tune-row">
            <label className="cv-tune-field">
              <span>Base match tightness: <b>{innerDeg}°</b></span>
              <input
                type="range" min={5} max={50} value={innerDeg}
                onChange={(e) => setInnerDeg(Number(e.target.value))}
              />
              <span className="cv-tune-hint">Lower = only recolors colors very close to the base</span>
            </label>
            <label className="cv-tune-field">
              <span>Print protection reach: <b>{outerDeg}°</b></span>
              <input
                type="range" min={20} max={110} value={outerDeg}
                onChange={(e) => setOuterDeg(Number(e.target.value))}
              />
              <span className="cv-tune-hint">
                Lower = protects more of the print (try this if leaves/petals look muddy); higher = recolors more of the fabric
              </span>
            </label>
          </div>

          {previewUrl && (
            <div className="cv-save-row">
              <select
                className="ap-select cv-source-select"
                value={saveTarget}
                onChange={(e) => setSaveTarget(e.target.value)}
              >
                <option value={NEW_COLOR}>+ Save as new color</option>
                {colors.map((c, idx) => (
                  <option key={idx} value={idx}>Add to "{c.name || `Color ${idx + 1}`}"</option>
                ))}
              </select>
              {saveTarget === NEW_COLOR && (
                <input
                  className="ap-input cv-name-input"
                  type="text"
                  placeholder="Color name, e.g. Blossom Pink"
                  maxLength={30}
                  value={newColorName}
                  onChange={(e) => setNewColorName(e.target.value)}
                />
              )}
              <button type="button" className="cv-generate-btn" onClick={saveGeneratedPreview}>
                Save as swatch
              </button>
            </div>
          )}
        </div>
      )}

      {colors.map((c, i) => (
        <div className="cv-row" key={i}>
          <div className="cv-row-top">
            <input
              type="color"
              className="cv-swatch-input"
              value={c.hex}
              onChange={(e) => updateColor(i, "hex", e.target.value)}
              title="Pick swatch color"
            />
            <input
              className="ap-input cv-name-input"
              type="text"
              placeholder="e.g. Yellow"
              maxLength={30}
              value={c.name}
              onChange={(e) => updateColor(i, "name", e.target.value)}
            />
            <span className="cv-hex-label">{c.hex}</span>
            <button type="button" className="cv-remove-row" onClick={() => removeColor(i)} title="Remove color">
              ✕
            </button>
          </div>

          <div className="cv-images-row">
            {c.images.map((img, imgIdx) => (
              <div className="cv-image-item" key={imgIdx}>
                <img src={imgSrc(img, backendUrl)} alt={c.name || "color"} />
                <button type="button" className="ap-remove-btn" onClick={() => removeImage(i, imgIdx)}>✕</button>
              </div>
            ))}
            {c.images.length < 4 && (
              <label className="cv-image-add" htmlFor={`cv-file-${i}`}>
                + Photo
                <input
                  id={`cv-file-${i}`}
                  type="file"
                  multiple
                  accept="image/*"
                  hidden
                  onChange={(e) => { addImages(i, e.target.files); e.target.value = ""; }}
                />
              </label>
            )}
          </div>
        </div>
      ))}

      <button type="button" className="cv-add-color" onClick={addColor}>
        + Add Color
      </button>
    </div>
  );
};

export default ColorVariants;
